import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { AccountService, ContextService } from '@core/services';
import { IChecklistOption } from '@core/widgets/onboarding-widget/shared/onboarding-checklist/onboarding-checklist.component';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { getAccountCheck } from '@store/reducers/data.reducer';
import { getMe, getMyProfile } from '@store/reducers/profile.reducer';
import { AccountCheck, User, UserProfile, UserSegmentEnum, UserStatusEnum } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { distinctUntilKeyChanged, filter, map, mergeMap, Observable, switchMap, takeUntil } from 'rxjs';

@Component({
  selector: 'app-onboarding-widget',
  templateUrl: './onboarding-widget.component.html',
  styleUrls: ['./onboarding-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OnboardingWidgetComponent extends ChangableComponent implements OnInit {
  private contextService = inject(ContextService);
  private store = inject(Store);
  deviceService = inject(DeviceDetectorService);

  public checklistOptions: IChecklistOption[];

  public me$: Observable<User>;

  public me: User;
  public profile: UserProfile;
  public completed: boolean;

  ngOnInit() {
    this.me$ = this.contextService.me$;

    this.me$
      .pipe(
        switchMap((me) => {
          this.me = me;
          return this.store.select(getAccountCheck);
        }),
      )
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (accountCheck) => {
          this.checklistOptions = this.generateChecklistOptions(this.me, accountCheck);
          if (this.checklistOptions.findIndex((el) => !el.checked) === -1) {
            this.completed = true;
          }
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
        },
      });

    // Запрашиваем профиль, только если изменился id пользователя
    this.store
      .select(getMe)
      .pipe(
        distinctUntilKeyChanged('id'),
        filter((me) => !!me),
        map((me) => {
          this.contextService.updateUser(me);
        }),
        mergeMap(() => this.store.select(getMyProfile)),
        takeUntil(this.destroyed$),
      )
      .subscribe({
        next: (profile) => {
          this.contextService.updateProfile(profile);
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public generateChecklistOptions(me: User, accountCheck: Partial<AccountCheck>): IChecklistOption[] {
    return [
      {
        id: 1,
        label: 'onboarding.checklist.auth',
        checked: me?.status !== UserStatusEnum.guest,
      },
      {
        id: 2,
        label: 'onboarding.checklist.profile',
        route: '/profile',
        checked: AccountService.hasSegment(me, UserSegmentEnum.has_verified_teacher_profile),
      },
      {
        id: 3,
        label: 'onboarding.checklist.billing',
        route: '/me/finance',
        checked: !accountCheck.no_payins && !accountCheck.no_payouts,
      },
      {
        id: 4,
        label: 'onboarding.checklist.classes',
        route: '/my-library/services',
        checked: !!me?.stats?.services_count,
      },
      {
        id: 5,
        label: 'onboarding.checklist.schedule',
        route: '/schedule/working-hours',
        checked: !accountCheck.no_working_days,
      },
      {
        id: 6,
        label: 'onboarding.checklist.app',
        route: '/profile/application',
        checked: AccountService.hasSegment(me, UserSegmentEnum.use_mobile_app),
      },
      {
        id: 7,
        label: 'onboarding.checklist.3-lessons',
        route: '/constructor/edit/new/constructor/new',
        checked: me?.stats?.lessons_count >= 3,
      },
      {
        id: 8,
        label: 'onboarding.checklist.3-classes',
        route: '/go/class',
        checked: me?.stats?.services_count >= 2,
      },
    ];
  }
}
